import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';

interface UserSalesReportItem {
  userId: number;
  username: string;
  fullName: string;
  salesAmount: number;
  winAmount: number;
  commission: number;
  hierarchyCommissionEarned: number;
  hierarchyCommissionOwed: number;
  netHierarchyCommission: number;
  companyEarned: number;
}

interface UserSalesReport {
  users: UserSalesReportItem[];
  totals: UserSalesReportItem;
  startDate: string;
  endDate: string;
}

const UserSalesReport: React.FC = () => {
  const theme = useTheme();
  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(today);

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { data, isLoading, error, refetch } = useQuery<UserSalesReport>({
    queryKey: ['user-sales-report', formatDate(startDate), formatDate(endDate)],
    queryFn: async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      params.append('startDate', formatDate(startDate));
      params.append('endDate', formatDate(endDate));
      
      const response = await fetch(`${API_BASE_URL}/api/settlement/user-sales-report?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user sales report');
      }

      return response.json();
    }
  });

  useEffect(() => {
    refetch();
  }, [startDate, endDate, refetch]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>
          Error loading user sales report: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                User Sales Commission Report
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => newValue && setStartDate(newValue)}
                slotProps={{
                  textField: { size: 'small', sx: { width: 200 } }
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => newValue && setEndDate(newValue)}
                slotProps={{
                  textField: { size: 'small', sx: { width: 200 } }
                }}
              />
            </Box>
          </Box>

          {/* Summary Cards */}
          {data && (
            <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h5">
                    {data.users.length}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Sales Amount
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    {formatCurrency(data.totals.salesAmount)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Win Payouts
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {formatCurrency(data.totals.winAmount)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Commissions Paid
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {formatCurrency(data.totals.commission + data.totals.netHierarchyCommission)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Company Earned
                  </Typography>
                  <Typography variant="h5" color={data.totals.companyEarned >= 0 ? "success.main" : "error.main"} fontWeight="bold">
                    {formatCurrency(data.totals.companyEarned)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Table */}
          {data && data.users.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      User
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Sales Amount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Win Amount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Commission
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Hierarchy Commission Earned
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Hierarchy Commission Owed
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Net Hierarchy Commission
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.grey[100] }}>
                      Company Earned
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.users.map((user) => (
                    <TableRow key={user.userId} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {user.fullName || user.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(user.salesAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(user.winAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(user.commission)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(user.hierarchyCommissionEarned)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(user.hierarchyCommissionOwed)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color: user.netHierarchyCommission >= 0 ? 'success.main' : 'error.main',
                            fontWeight: 'medium'
                          }}
                        >
                          {formatCurrency(user.netHierarchyCommission)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: user.companyEarned >= 0 ? 'success.main' : 'error.main', 
                            fontWeight: 'bold' 
                          }}
                        >
                          {formatCurrency(user.companyEarned)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totals Row */}
                  {data.totals && (
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50], '& td': { fontWeight: 'bold', borderTop: `2px solid ${theme.palette.divider}` } }}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          TOTAL
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(data.totals.salesAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(data.totals.winAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(data.totals.commission)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        {formatCurrency(data.totals.hierarchyCommissionEarned)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>
                        {formatCurrency(data.totals.hierarchyCommissionOwed)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color: data.totals.netHierarchyCommission >= 0 ? 'success.main' : 'error.main',
                            fontWeight: 'bold'
                          }}
                        >
                          {formatCurrency(data.totals.netHierarchyCommission)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: data.totals.companyEarned >= 0 ? 'success.main' : 'error.main', 
                            fontWeight: 'bold' 
                          }}
                        >
                          {formatCurrency(data.totals.companyEarned)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : data && data.users.length === 0 ? (
            <Alert severity="info" sx={{ mt: 3 }}>
              No data found for the selected date range. Please ensure settlement has been processed for these dates.
            </Alert>
          ) : null}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default UserSalesReport;
